/**
 * Helpers for the Norwegian date-of-birth field on the payout form.
 *
 * Sellers type their birth date as DD.MM.ÅÅÅÅ; we format as they type,
 * validate it's a real adult date, and parse it into Stripe's
 * `{ day, month, year }` shape for the account token. The value is never
 * stored on our side — it rides straight into the client-side Stripe
 * token (see `getStripePrefillFromUser` / `createStripeAccount`).
 */

const MIN_AGE = 18;
const MAX_AGE = 120;

// Format raw input as DD.MM.ÅÅÅÅ while the seller types — strip
// non-digits, cap at 8 digits (DDMMYYYY), and insert the dots. Used as
// the field's `parse`, mirroring `formatNorwegianAccount`.
export const formatDateOfBirth = value => {
  if (!value) {
    return value;
  }
  const digits = value.replace(/\D/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(
    (_, i) => i === 0 || digits.length > i * 2
  );
  return parts.join('.');
};

// Parse "DD.MM.ÅÅÅÅ" into { day, month, year }, or null if it isn't a
// real calendar date (rejects e.g. 31.02.1990). Calendar-validity only —
// age/future checks live in `isAdultDateOfBirth`.
export const parseDateOfBirth = value => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length !== 8) {
    return null;
  }
  const day = parseInt(digits.slice(0, 2), 10);
  const month = parseInt(digits.slice(2, 4), 10);
  const year = parseInt(digits.slice(4, 8), 10);
  const date = new Date(year, month - 1, day);
  const isRealDate =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDate ? { day, month, year } : null;
};

// True when the value is a real date, not in the future, and the person
// is between MIN_AGE and MAX_AGE — i.e. a plausible adult birth date
// Stripe will accept.
export const isAdultDateOfBirth = value => {
  const parsed = parseDateOfBirth(value);
  if (!parsed) {
    return false;
  }
  const { day, month, year } = parsed;
  if (year < 1900) {
    return false;
  }
  const dob = new Date(year, month - 1, day);
  const now = new Date();
  if (dob > now) {
    return false;
  }
  let age = now.getFullYear() - year;
  const hadBirthdayThisYear =
    now.getMonth() > month - 1 || (now.getMonth() === month - 1 && now.getDate() >= day);
  if (!hadBirthdayThisYear) {
    age -= 1;
  }
  return age >= MIN_AGE && age <= MAX_AGE;
};
